<template>
  <v-container class="mb-8" fluid>
    <v-row justify="center" class="mb-2">
      <CircularButton
        :colors="clockButtonColors"
        :button-pressed="clockPressed"
        :button-size="clockButtonSize"
        :active-segment="activeSegmentType"
        :tooltip="clockTooltipText"
        :bottom-tooltip="clockBottomTooltipText"
        :loading="loading"
        :disabled="clockDisabled"
        @handlePress="handleClockButtonPress"
        @handleBottomPress="handleSegmentTimeButtonPress"
      >
        <template v-slot:middle>
          <v-row align="center" justify="center">
            <span class="font-weight-bold text-h3">{{ timer.duration }}</span>
          </v-row>
          <v-row align="center" justify="center" :class="isMobile() ? 'pt-1' : 'pt-4'">
            <v-chip :color="clockChipColor" dark>
              {{ clockChipText }}
              <v-icon>{{ clockChipIcon }}</v-icon>
            </v-chip>
          </v-row>
        </template>
        <template v-slot:bottom>
          <v-chip
            v-show="isSegmentTimePairFeatureActive && !clockDisabled && clockPressed"
            :color="segmentChipColor"
            dark
          >
            {{ segmentChipText }}
            <v-icon>{{ segmentChipIcon }}</v-icon>
          </v-chip>
        </template>
      </CircularButton>
    </v-row>

    <TimePairConfig
      v-if="loadedAssignments"
      ref="config"
      :timePair="activeTimePair"
      :assignments="configAssignments"
      :assignment-text="assignment => assignment.position.description ? assignment.position.description :
        `(${assignment.position.id})`"
      :disabled="!!activeTimePair.id || clockDisabled"
      :fullWidth="isMenuDrawerClosable()"
      @update-employee-assignment="setEmployeeSelectedAssignment"
      @update-pair-time-type="setTimePairTimeType"
      @update-time-pair-config="setTimePairConfig"
    />
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';
import some from 'lodash/some';

import constants from '@/constants';
import EventBus from '@/event-bus';

import DateTimeUtil from '@/util/date-time-util';

import displayAdaptable from '@/mixins/displayAdaptable';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import resolveMobileBackButton from '@/mixins/resolveMobileBackButton';
import validatable from '@/mixins/validatable';

import CircularButton from '@/components/timepairs/clock/CircularButton.vue';
import TimePairConfig from '@/components/timepairs/TimePairConfig.vue';
import AuthorisationUtil from '@/util/authorisation-util';
import hasAutoClockOut from '@/mixins/hasAutoClockOut';
import hasNotificationFeatures from '../../mixins/hasNotificationFeatures';

export default {
  name: 'Clock',

  components: {
    CircularButton,
    TimePairConfig,
  },

  mixins: [
    displayAdaptable,
    hasAppDialog,
    hasAppSnackbar,
    hasTimePairFeatures,
    resolveMobileBackButton,
    validatable,
    hasAutoClockOut,
    hasNotificationFeatures,
  ],

  data() {
    return {
      timePairConfig: Object,
      selectedAssignment: null,
      selectedTimeType: null,
      loading: false,
      loadedAssignments: false,
      pingServerTimeout: null,
      decodedAuthToken: null,
      clockButtonColors: {
        pressed: {
          background: '#ec5c59',
          secondaryBackground: '#ec9549',
          loading: '#f8887f',
          secondaryLoading: '#ffbc60',
          chip: '#cb4649',
          chipLoading: '#ff6058',
          border: '#cf3539',
        },
        unpressed: {
          background: '#4cc655',
          loading: '#b4e59c',
          chipLoading: '#8fd56b',
          border: '#639e31',
        },
        onBreak: {
          background: '#ec9549',
          loading: '#ffbc60',
          chip: '#c97c2e',
          chipLoading: '#ffa146',
          border: '#cf3539',
        },
        disabled: {
          background: '#d2d2d2',
          border: '#bbb9b9',
          chip: '#bbb9b9',
        },
      },
    };
  },

  computed: {
    ...mapState({
      activeTimePair: (state) => state.activeTimePair,
      assignments: (state) => state.employee.assignments,
      timer: (state) => state.timer,
      warnedOfMissedClockOut: (state) => state.warnedOfMissedClockOut,
      activeSegment: (state) => state.activeSegment,
      activeSegmentType: (state) => state.activeSegmentType,
      userConfiguration: (state) => state.userConfiguration,
      notificationCount: (state) => state.notificationCount,
    }),

    clockDisabled() {
      if (this.clockPressed) {
        return !some(this.assignments, (assignment) => assignment.id === this.activeTimePair.employeeAssignment.id);
      }

      return this.assignments.length === 0;
    },

    clockPressed() {
      return this.activeTimePair.id !== null;
    },

    clockButtonSize() {
      return {
        width: this.isMobile() ? '260px' : '380px',
        height: this.isMobile() ? '260px' : '380px',
      };
    },

    clockTooltipText() {
      if (this.clockDisabled && !this.clockPressed) return this.$t('clock.notAuthorized');
      if (!this.clockPressed) return this.$t('clock.clockIn');
      if (!this.isSegmentTimePairFeatureActive) return this.$t('clock.clockOut');
      if (this.activeSegmentType !== constants.TimePairType.SHIFT && !this.clockDisabled) {
        return this.$t('clock.resume');
      }
      return this.$t('clock.clockOut');
    },

    clockBottomTooltipText() {
      if (this.activeSegmentType === constants.TimePairType.SHIFT) return this.$t('clock.takeBreak');
      return this.$t('clock.clockOut');
    },

    clockChipColor() {
      if (this.clockDisabled && !this.clockPressed && !this.loading) {
        return this.clockButtonColors.disabled.chip;
      }

      if (!this.clockPressed) {
        return this.loading
          ? this.clockButtonColors.unpressed.chipLoading
          : this.clockButtonColors.unpressed.border;
      }

      if (!this.isSegmentTimePairFeatureActive || this.clockDisabled) {
        return this.clockButtonColors.pressed.chip;
      }

      switch (this.activeSegmentType) {
        case constants.TimePairType.BREAK: {
          return this.loading
            ? this.clockButtonColors.onBreak.chipLoading
            : this.clockButtonColors.onBreak.chip;
        }
        default: {
          return this.loading
            ? this.clockButtonColors.pressed.chipLoading
            : this.clockButtonColors.pressed.chip;
        }
      }
    },

    clockChipText() {
      if (!this.clockPressed) return this.$t('clock.start').toUpperCase();
      if (!this.isSegmentTimePairFeatureActive) return this.$t('clock.stop').toUpperCase();
      if (this.activeSegmentType !== constants.TimePairType.SHIFT
        && !this.clockDisabled) return this.$t('clock.resume').toUpperCase();
      return this.$t('clock.stop').toUpperCase();
    },

    clockChipIcon() {
      if (this.clockPressed && !this.isSegmentTimePairFeatureActive) return 'stop';
      if (!this.clockPressed
        || (this.activeSegmentType !== constants.TimePairType.SHIFT && !this.clockDisabled)) return 'play_arrow';
      return 'stop';
    },

    segmentChipColor() {
      switch (this.activeSegmentType) {
        case constants.TimePairType.BREAK: {
          return this.loading
            ? this.clockButtonColors.pressed.chipLoading
            : this.clockButtonColors.pressed.chip;
        }
        default: {
          return this.loading
            ? this.clockButtonColors.onBreak.chipLoading
            : this.clockButtonColors.onBreak.chip;
        }
      }
    },

    segmentChipText() {
      return this.activeSegmentType === constants.TimePairType.SHIFT ? this.$t('clock.pause').toUpperCase()
        : this.$t('clock.stop').toUpperCase();
    },

    segmentChipIcon() {
      return this.activeSegmentType === constants.TimePairType.SHIFT ? 'pause' : 'stop';
    },

    configAssignments() {
      if (this.clockPressed) {
        return [this.activeTimePair.employeeAssignment];
      }

      return this.assignments;
    },
  },

  methods: {
    ...mapMutations([
      'setPageTitle',
      'setMissedClockOutWarned',
      'resetActiveTimePair',
      'setActiveTimePair',
      'setActiveSegmentType',
      'updateNotificationCount',
    ]),

    ...mapActions([
      'updateUserAssignments',
      'fetchUserActiveInfo',
      'updateTimer',
      'fetchNotifications',
    ]),

    setEmployeeSelectedAssignment(assignment) {
      this.selectedAssignment = assignment;
    },

    setTimePairTimeType(timeType) {
      this.selectedTimeType = timeType;
    },

    setTimePairConfig(value) {
      this.timePairConfig = value;
    },

    displayMissedClockOutWarning() {
      if (this.isMissedClockOutNotificationActive && !this.warnedOfMissedClockOut && this.checkMissingClockOut()) {
        this.setMissedClockOutWarned(true);
        this.showAppWarningDialog({
          message: this.isBreak() && !this.isBreakOutFeatureDisabled ? this.$t('dialogs.warning.missedBreakOut.message',
            {
              time: this.adjustedClockOutThreshold(this.activeSegment.clockOutThreshold),
            }) : this.$t('dialogs.warning.missedClockOut.message',
            {
              time: DateTimeUtil.validation.missingClockOutThreshold,
            }),
        });
      }
    },

    isBreak() {
      return this.activeSegmentType === constants.TimePairType.BREAK;
    },

    adjustedClockOutThreshold(threshold) {
      return threshold !== -1 ? threshold : DateTimeUtil.validation.missingBreakOutThreshold;
    },

    checkMissingClockOut() {
      if (this.activeTimePair.clockIn == null) {
        return false;
      }

      return DateTimeUtil.timeEntryOlderThan(
        this.activeTimePair.clockIn,
        this.activeSegmentType === constants.TimePairType.BREAK && !this.isBreakOutFeatureDisabled
          ? this.adjustedClockOutThreshold(this.activeSegment.clockOutThreshold)
          : DateTimeUtil.validation.missingClockOutThreshold,
        this.activeSegmentType === constants.TimePairType.BREAK && !this.isBreakOutFeatureDisabled
          ? DateTimeUtil.validation.minutesUnit : DateTimeUtil.validation.hoursUnit,
      );
    },

    handleClockButtonPress() {
      if ((this.clockDisabled && !this.clockPressed) || this.loading) return;

      this.loading = true;

      if (!this.activeTimePair.id) {
        if (!this.configItemsValid()) {
          this.loading = false;
        } else if (this.isAutoClockOutEnabled && this.hasScheduledTimePast()) {
          const cancelHandler = () => { this.loading = false; };

          this.showAutoClockOutWarningDialog(
            this.emitClockIn,
            cancelHandler,
          );
        } else {
          this.emitClockIn();
        }
      } else if (!this.isSegmentTimePairFeatureActive || this.clockDisabled) {
        this.createClockOutEntry();
      } else {
        switch (this.activeSegmentType) {
          case constants.TimePairType.BREAK: {
            this.emitBreakOut();
            break;
          }
          default: {
            this.createClockOutEntry();
          }
        }
      }
    },

    handleSegmentTimeButtonPress() {
      if (this.clockDisabled || this.loading) return;

      this.loading = true;

      if (this.activeSegmentType !== constants.TimePairType.SHIFT) {
        this.createClockOutEntry();
      } else {
        this.emitSegmentClockIn();
      }
    },

    configItemsValid() {
      const isValid = this.$refs.config.$refs.form.validate();

      if (isValid) {
        return true;
      }

      this.showSnackbar({
        text: this.$t('snackbar.completeRequiredFields'),
        color: constants.AppColors.ERROR,
      });
      return false;
    },

    emitClockIn() {
      EventBus.$emit('clock:create-clock-in', {
        timePairCustomFieldValues: this.timePairConfig,
        selectedAssignment: this.selectedAssignment,
        selectedTimeType: this.selectedTimeType,
        isCloningTimePair: false,
        lastly: () => {
          this.loading = false;
        },
      });
    },

    emitClockOut() {
      EventBus.$emit('clock:create-clock-out', {
        timePair: cloneDeep(this.activeTimePair),
        isCloningTimePair: false,
        lastly: () => {
          this.loading = false;
        },
      });
      this.timePairConfig = {};
    },

    emitSegmentClockIn() {
      EventBus.$emit('clock:create-segment-clock-in', {
        timePair: cloneDeep(this.activeTimePair),
        pairType: constants.TimePairType.BREAK,
        lastly: () => {
          this.loading = false;
        },
      });
    },

    emitBreakOut() {
      const activeSegment = this.activeTimePair.segmentTimePairs.reverse().find((segment) => !segment.clockOut);

      EventBus.$emit('clock:create-segment-clock-out', {
        timePair: cloneDeep(activeSegment),
        selectedAssignment: this.selectedAssignment,
        lastly: () => {
          this.loading = false;
        },
      });
    },

    createClockOutEntry() {
      this.emitClockOut();
      this.$refs.config.$refs.form.reset();
    },

    pingServerForActiveShift() {
      this.fetchUserActiveInfo(this.decodedAuthToken)
      .then((response) => this.handleServerPingResponse(response))
      .finally(() => {
        this.pingServerTimeout = setTimeout(this.pingServerForActiveShift, constants.SERVER_ACTIVE_SHIFT_PING_DELAY);
      });
    },

    handleServerPingResponse(response) {
      const shiftPair = response.data.shiftTimePairSummary;
      if (!shiftPair) {
        if (this.activeTimePair.id) {
          if (this.notificationCount !== response.data.notificationCount) {
            this.updateNotificationCount(response.data.notificationCount);
            this.fetchNotifications();
          }
          this.resetActiveTimePair();
        }
      } else if (shiftPair) {
        if (this.activeTimePair.id !== shiftPair.id) {
          this.setActiveTimePair(shiftPair);
          this.updateTimer(this.activeTimePair.clockIn);
        } else if (shiftPair.isSegmentPairActive) {
          const currentSegment = shiftPair.segmentTimePairs.reverse().find((segment) => !segment.clockOut);
          if (
            this.activeTimePair.segmentTimePairs.length !== shiftPair.segmentTimePairs.length
            || this.activeSegmentType !== currentSegment.pairType
          ) {
            this.setActiveTimePair(shiftPair);
            this.setActiveSegmentType(currentSegment.pairType);
            this.updateTimer(currentSegment.clockIn);
          }
        } else if (
          !shiftPair.isSegmentPairActive
           && this.activeSegmentType !== constants.TimePairType.SHIFT
        ) {
          this.setActiveSegmentType(constants.TimePairType.SHIFT);
          this.updateTimer(this.activeTimePair.clockIn);
        }
      }
    },
  },

  async created() {
    this.setPageTitle(this.$t('titles.recordTime'));
    this.displayMissedClockOutWarning();
    await this.updateUserAssignments(this.userConfiguration.user);
    this.loadedAssignments = true;

    this.decodedAuthToken = AuthorisationUtil.fetchDecodedAuthToken();

    this.pingServerTimeout = setTimeout(this.pingServerForActiveShift, constants.SERVER_ACTIVE_SHIFT_PING_DELAY);
  },

  beforeDestroy() {
    clearTimeout(this.pingServerTimeout);
  },
};

</script>

<style scoped>
</style>
